/**
 * Panel-local slice for the extension Agents endpoints
 * (`/extension/agents/*`) plus the two portal reads the run lifecycle needs.
 * Mirrors prospect-api: same authed base query, no Clerk, minimal types.
 */
import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithAuth } from '@/store/base-query';

export interface AgentRunHandle {
  run_id: string;
  task_id: string;
  lead_list_id: string;
  company_id: string;
  account_name?: string | null;
  domain?: string | null;
}

export interface AgentRunStatus {
  run_id: string;
  status: string;
  total_leads?: number | null;
  processed_leads?: number | null;
  failed_leads?: number | null;
}

export interface CompanyAgentResult {
  id: string;
  company_id: string | null;
  column_type: string;
  status: string;
  processed_value?: { markdown?: string | null } | string | null;
  column_metadata?: {
    custom_agent_id?: string | null;
    run_id?: string | null;
    error?: string | null;
  } | null;
  error_message?: string | null;
  created_at?: string | null;
}

export interface SaveToCrmResult {
  field: string;
  field_status: string;
  permissions_status?: string | null;
  written: boolean;
  truncated: boolean;
}

export const pluginAgentsApi = createApi({
  reducerPath: 'pluginAgentsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['AgentResults'],
  endpoints: (builder) => ({
    runOnAccount: builder.mutation<
      AgentRunHandle,
      {
        crm: 'salesforce';
        record_id: string;
        agent_type: string;
        custom_agent_id?: string;
        idempotency_key?: string;
      }
    >({
      query: (body) => ({
        url: '/extension/agents/run-on-account',
        method: 'POST',
        body,
      }),
    }),
    saveToCrm: builder.mutation<
      SaveToCrmResult,
      {
        crm: 'salesforce';
        record_id: string;
        agent_type: string;
        custom_agent_id?: string;
        lead_list_id: string;
        company_id: string;
      }
    >({
      query: (body) => ({
        url: '/extension/agents/save-to-crm',
        method: 'POST',
        body,
      }),
    }),
    getRunStatus: builder.query<AgentRunStatus, string>({
      query: (runId) => `/agents/runs/${encodeURIComponent(runId)}`,
    }),
    getCompanyAgentResults: builder.query<
      { results: CompanyAgentResult[] },
      string
    >({
      query: (leadListId) =>
        `/ai/smart-columns/company-results/${encodeURIComponent(leadListId)}`,
      providesTags: (_r, _e, leadListId) => [
        { type: 'AgentResults', id: leadListId },
      ],
    }),
  }),
});

export const {
  useRunOnAccountMutation,
  useSaveToCrmMutation,
  useLazyGetRunStatusQuery,
  useGetCompanyAgentResultsQuery,
} = pluginAgentsApi;
